import type { SimulationPlatform } from '@lensora/shared'
import { useQuery } from '@tanstack/react-query'
import { TrendingUp, BarChart2, Zap, Target } from 'lucide-react'
import * as React from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'

import { api } from '../../../lib/api'

import { Ga4TrafficWidget } from '@/components/dashboard/Ga4TrafficWidget'

// ─── Types ────────────────────────────────────────────────────────────────────

interface AnalyticsData {
  score_improvement: number
  credits_used: number
  simulations_run: number
  fixes_completed: number
  total_fixes: number
  daily_credits: { date: string; amount: number }[]
  score_history: { date: string; score: number }[]
  fixes_by_status: { completed: number; in_progress: number; pending: number }
  platform_performance: { date: string; platform: string; score: number }[]
}

// Keyed by SimulationPlatform (plus 'groq', which appears in historical
// performance data even though it's not a user-selectable simulation
// platform) so a future platform addition is a compile error here, not a
// silent generic-slug/muted-colour fallback in the chart legend — see MR
// !163 review, where 'chatgpt-search'/'claude-search' were missed here.
const PLATFORM_LABELS: Record<SimulationPlatform, string> & Record<string, string> = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  gemini: 'Gemini',
  perplexity: 'Perplexity',
  groq: 'Groq',
  'google-ai': 'Google AI',
  'chatgpt-search': 'ChatGPT Search',
  'claude-search': 'Claude Search',
}

// Search-grounded variants get a dedicated accent colour distinct from their
// base platform, matching the convention in PlatformIcon.tsx / format.ts.
const PLATFORM_COLORS: Record<SimulationPlatform, string> & Record<string, string> = {
  chatgpt: '#10a37f',
  claude: '#d97757',
  gemini: '#4285f4',
  perplexity: '#8b5cf6',
  groq: '#f97316',
  'google-ai': '#34a853',
  'chatgpt-search': '#0ea5e9',
  'claude-search': '#f59e0b',
}

// Pivots the flat {date, platform, score}[] into one row per week with a
// column per platform — the shape recharts' multi-line LineChart wants.
function pivotPlatformPerformance(points: { date: string; platform: string; score: number }[]) {
  const platforms = [...new Set(points.map((p) => p.platform))]
  const dateOrder: string[] = []
  const rowsByDate = new Map<string, Record<string, number | string>>()

  for (const p of points) {
    if (!rowsByDate.has(p.date)) {
      dateOrder.push(p.date)
      rowsByDate.set(p.date, { date: p.date })
    }
    rowsByDate.get(p.date)![p.platform] = p.score
  }

  return { platforms, rows: dateOrder.map((d) => rowsByDate.get(d)!) }
}

// Design token hex values — used for recharts SVG fills/strokes only
const COLOR_BRAND = '#6366f1'    // --brand-accent
const COLOR_SUCCESS = '#16a34a'  // --feedback-success
const COLOR_WARNING = '#ca8a04'  // --feedback-warning
const COLOR_MUTED = '#64748b'    // --text-muted

const BENCHMARK_DATA = [
  { label: 'Your Brand', value: 67, color: COLOR_BRAND },
  { label: 'Industry Avg.', value: 52, color: COLOR_MUTED },
  { label: 'Top 10%', value: 84, color: COLOR_SUCCESS },
  { label: 'Bottom 25%', value: 31, color: '#dc2626' },
]

// ─── Components ───────────────────────────────────────────────────────────────

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ name?: string; value: number; color?: string }>
  label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2.5 text-xs shadow-lg">
      {label && <p className="mb-2 text-text-muted">{label}</p>}
      {payload.map((p, i) => (
        <div key={i} className="mb-1 flex items-center gap-2 last:mb-0">
          {p.color && <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: p.color }} />}
          <span className="text-text-muted">{p.name ?? 'Value'}:</span>
          <span className="font-bold tabular-nums text-text">{p.value}</span>
        </div>
      ))}
    </div>
  )
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  iconClass,
  loading,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
  sub: string
  iconClass: string
  loading?: boolean
}) {
  if (loading) {
    return (
      <div className="animate-pulse rounded-xl border border-border bg-surface p-5">
        <div className="mb-3 h-10 w-10 rounded-lg bg-surface-overlay" />
        <div className="mb-2 h-7 w-1/2 rounded bg-surface-overlay" />
        <div className="mb-1 h-4 w-3/4 rounded bg-surface-overlay" />
        <div className="h-3 w-1/2 rounded bg-surface-overlay" />
      </div>
    )
  }
  return (
    <div className="rounded-xl border border-border bg-surface p-5">
      <div className="mb-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-surface-raised ${iconClass}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="mb-1 text-2xl font-bold text-text">{value}</p>
      <p className="text-sm font-medium text-text-muted">{label}</p>
      <p className="mt-0.5 text-xs text-text-muted opacity-60">{sub}</p>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function AnalyticsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['analytics'],
    queryFn: () => api.get<AnalyticsData>('/api/analytics'),
  })

  const scoreImprovement = data?.score_improvement ?? 0
  const creditsUsed = data?.credits_used ?? 0
  const simulationsRun = data?.simulations_run ?? 0
  const fixesCompleted = data?.fixes_completed ?? 0
  const totalFixes = data?.total_fixes ?? 0
  const dailyCredits = data?.daily_credits ?? []
  const scoreHistory = data?.score_history ?? []
  const fixesByStatus = data?.fixes_by_status ?? { completed: 0, in_progress: 0, pending: 0 }
  const platformPerformance = data?.platform_performance ?? []
  const { platforms: performancePlatforms, rows: performanceRows } = pivotPlatformPerformance(platformPerformance)

  const fixesData = [
    { name: 'Completed', value: fixesByStatus.completed, color: COLOR_SUCCESS },
    { name: 'In Progress', value: fixesByStatus.in_progress, color: COLOR_BRAND },
    { name: 'Pending', value: fixesByStatus.pending, color: COLOR_MUTED },
  ]

  const totalCreditsInChart = dailyCredits.reduce((acc, d) => acc + d.amount, 0)
  const fixesLabel = totalFixes > 0
    ? `${Math.round((fixesCompleted / totalFixes) * 100)}% completion`
    : 'No fixes yet'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text">Analytics</h1>
        <p className="mt-1 text-sm text-text-muted">Deep-dive into your AI visibility performance over time</p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          icon={TrendingUp}
          label="Score Improvement"
          value={isLoading ? '—' : (scoreImprovement >= 0 ? `+${scoreImprovement}` : `${scoreImprovement}`)}
          sub="Since first score"
          iconClass="text-feedback-success"
          loading={isLoading}
        />
        <StatCard
          icon={Zap}
          label="Credits Used"
          value={isLoading ? '—' : creditsUsed.toString()}
          sub="Last 30 days"
          iconClass="text-feedback-warning"
          loading={isLoading}
        />
        <StatCard
          icon={BarChart2}
          label="Simulations Run"
          value={isLoading ? '—' : simulationsRun.toString()}
          sub="This month"
          iconClass="text-brand-accent"
          loading={isLoading}
        />
        <StatCard
          icon={Target}
          label="Fixes Completed"
          value={isLoading ? '—' : `${fixesCompleted} / ${totalFixes}`}
          sub={isLoading ? '' : fixesLabel}
          iconClass="text-brand-primary"
          loading={isLoading}
        />
      </div>

      {/* Credit usage */}
      <div className="rounded-xl border border-border bg-surface p-6">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-text">Credit Usage</h2>
            <p className="mt-0.5 text-xs text-text-muted">Daily credit consumption over the last 30 days</p>
          </div>
          <span className="text-sm font-bold text-feedback-warning">{totalCreditsInChart} total</span>
        </div>
        {dailyCredits.length === 0 && !isLoading ? (
          <p className="py-16 text-center text-xs text-text-muted">No credit usage data yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dailyCredits} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(226,232,240,0.6)" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: COLOR_MUTED }} tickLine={false} axisLine={false} interval={6} />
              <YAxis tick={{ fontSize: 10, fill: COLOR_MUTED }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(226,232,240,0.4)' }} />
              <Bar dataKey="amount" fill={COLOR_WARNING} radius={[2, 2, 0, 0]} name="Credits" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Score history + fixes donut */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-surface p-6 lg:col-span-2">
          <div className="mb-5 flex items-start justify-between">
            <div>
              <h2 className="text-sm font-semibold text-text">Score Improvement Over Time</h2>
              <p className="mt-0.5 text-xs text-text-muted">Visibility score snapshots since onboarding</p>
            </div>
            <span className="text-sm font-bold text-feedback-success">
              {scoreImprovement >= 0 ? `+${scoreImprovement}` : scoreImprovement} pts
            </span>
          </div>
          {scoreHistory.length === 0 ? (
            <p className="py-16 text-center text-xs text-text-muted">
              No data yet — run your first simulation to see score history
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={scoreHistory} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(226,232,240,0.6)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: COLOR_MUTED }} tickLine={false} axisLine={false} interval={2} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: COLOR_MUTED }} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke={COLOR_BRAND}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5, fill: COLOR_BRAND, stroke: '#ffffff', strokeWidth: 2 }}
                  name="Score"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-xl border border-border bg-surface p-6">
          <h2 className="mb-5 text-sm font-semibold text-text">Fixes Status</h2>
          {totalFixes === 0 && !isLoading ? (
            <p className="py-16 text-center text-xs text-text-muted">No fixes yet</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={fixesData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {fixesData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke="transparent" />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null
                      const item = payload[0]
                      if (!item) return null
                      const d = item.payload as { name: string; value: number }
                      return (
                        <div className="rounded-lg border border-border bg-surface px-3 py-2 text-xs shadow-lg">
                          <p className="font-semibold text-text">{d.name}</p>
                          <p className="text-text-muted">{d.value} fixes</p>
                        </div>
                      )
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-2">
                {fixesData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-text-muted">{item.name}</span>
                    </div>
                    <span className="font-semibold text-text-muted">{item.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Platform performance */}
      <div className="rounded-xl border border-border bg-surface p-6">
        <div className="mb-5">
          <h2 className="text-sm font-semibold text-text">Platform Performance Over Time</h2>
          <p className="mt-0.5 text-xs text-text-muted">Weekly visibility score per LLM platform</p>
        </div>
        {isLoading ? (
          <div className="h-[260px] animate-pulse rounded-lg bg-surface-overlay" />
        ) : performanceRows.length === 0 ? (
          <div className="flex h-[260px] flex-col items-center justify-center text-center">
            <BarChart2 className="mb-3 h-10 w-10 text-text-muted opacity-40" />
            <p className="text-xs font-medium text-text">No platform data yet</p>
            <p className="mt-1 max-w-xs text-xs text-text-muted">
              Run a simulation to start tracking your weekly visibility score across ChatGPT, Claude, Gemini, and other platforms.
            </p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={performanceRows} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(226,232,240,0.6)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: COLOR_MUTED }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: COLOR_MUTED }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                {performancePlatforms.map((platform) => (
                  <Line
                    key={platform}
                    type="monotone"
                    dataKey={platform}
                    name={PLATFORM_LABELS[platform] ?? platform}
                    stroke={PLATFORM_COLORS[platform] ?? COLOR_MUTED}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-3 flex flex-wrap gap-3">
              {performancePlatforms.map((platform) => (
                <div key={platform} className="flex items-center gap-1.5 text-xs">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: PLATFORM_COLORS[platform] ?? COLOR_MUTED }}
                  />
                  <span className="text-text-muted">{PLATFORM_LABELS[platform] ?? platform}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Ga4TrafficWidget />

      {/* Industry benchmark — illustrative only: we don't aggregate real
          cross-tenant scores yet, so this shows sample figures rather than
          claiming to reflect actual industry data. */}
      <div className="rounded-xl border border-border bg-surface p-6">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-text">Industry Benchmark</h2>
            <p className="mt-0.5 text-xs text-text-muted">An example of how your score could compare to the SaaS industry</p>
          </div>
          <span className="shrink-0 rounded-full border border-border bg-surface-raised px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-muted">
            Sample data
          </span>
        </div>
        <div className="space-y-4">
          {BENCHMARK_DATA.map((item) => (
            <div key={item.label} className="flex items-center gap-4">
              <span className="w-24 shrink-0 text-xs text-text-muted">{item.label}</span>
              <div className="relative h-6 flex-1 overflow-hidden rounded-lg bg-surface-raised">
                <div
                  className="flex h-full items-center justify-end rounded-lg pr-2 transition-all duration-700"
                  style={{ width: `${item.value}%`, backgroundColor: item.color, minWidth: 40 }}
                >
                  <span className="text-xs font-bold tabular-nums text-text-inverse">{item.value}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-text-muted">
          Illustrative example — real industry comparisons aren't available yet. If they were, a brand in this
          position would be <span className="font-semibold text-brand-accent">+15 pts above industry average</span> and{' '}
          <span className="font-semibold text-feedback-warning">17 pts below the top 10%</span>.
        </p>
      </div>
    </div>
  )
}
